import Dexie, { type EntityTable } from 'dexie'
import type { AppSetting, CaptureProfile, RankQueueItem, SessionJourney, SessionRecord } from '@/domain/types'

class MapleLogDatabase extends Dexie {
  profiles!: EntityTable<CaptureProfile, 'id'>
  sessions!: EntityTable<SessionRecord, 'id'>
  sessionJourneys!: EntityTable<SessionJourney, 'sessionId'>
  rankQueue!: EntityTable<RankQueueItem, 'id'>
  settings!: EntityTable<AppSetting, 'key'>

  constructor() {
    super('maple-log')
    this.version(1).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      observations: '++id, sessionId, [sessionId+capturedAt], capturedAt',
      events: 'id, sessionId, [sessionId+capturedAt], type, capturedAt',
      settings: 'key, updatedAt',
    })
    this.version(2).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      observations: null,
      events: null,
      settings: 'key, updatedAt',
    })
    this.version(3).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
    }).upgrade((transaction) => transaction.table('sessions').clear())
    this.version(4).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
      rankQueue: 'id, nextAttemptAt, createdAt',
    })
    this.version(5).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      settings: 'key, updatedAt',
      rankQueue: null,
    })
    this.version(6).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      sessionJourneys: 'sessionId, updatedAt',
      settings: 'key, updatedAt',
    })
    this.version(7).stores({
      profiles: 'id, [presetId+presetVersion], updatedAt',
      sessions: 'id, presetId, status, startedAt, endedAt',
      sessionJourneys: 'sessionId, updatedAt',
      rankQueue: 'id, sessionId, status, nextAttemptAt, createdAt',
      settings: 'key, updatedAt',
    })
  }
}

export const db = new MapleLogDatabase()

export async function saveSessionState(session: SessionRecord, journey: SessionJourney | null): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, async () => {
    await db.sessions.put(session)
    if (journey) await db.sessionJourneys.put(journey)
  })
}

export async function deleteSessionState(sessionId: string): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
    await db.sessions.delete(sessionId)
    await db.sessionJourneys.delete(sessionId)
    await db.rankQueue.where('sessionId').equals(sessionId).delete()
  })
}

export async function clearSessionHistory(): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
    await db.sessions.clear()
    await db.sessionJourneys.clear()
    await db.rankQueue.clear()
  })
}

export async function saveEndedSessionState(
  session: SessionRecord,
  journey: SessionJourney | null,
  rankItems: readonly RankQueueItem[],
): Promise<void> {
  await db.transaction('rw', db.sessions, db.sessionJourneys, db.rankQueue, async () => {
    await db.sessions.put(session)
    if (journey) await db.sessionJourneys.put(journey)
    if (session.rankIneligibleReason) await db.rankQueue.where('sessionId').equals(session.id).delete()
    else if (rankItems.length) await db.rankQueue.bulkPut([...rankItems])
  })
}

export class RecoveryBuffer {
  private lastFlushAt = 0

  async flush(session: SessionRecord, journey: SessionJourney | null, force = false): Promise<void> {
    const now = Date.now()
    if (!force && now - this.lastFlushAt < 5_000) return
    const previousFlushAt = this.lastFlushAt
    this.lastFlushAt = now
    try {
      await saveSessionState(session, journey)
    } catch (error) {
      this.lastFlushAt = previousFlushAt
      throw error
    }
  }
}
